import { Search } from "@mui/icons-material";
import {
  Checkbox,
  Icon,
  InputAdornment,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  tableCellClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { flexRender } from "@tanstack/react-table";
import MDBox from "components/MDBox";
import { useEffect, useState } from "react";

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    //backgroundColor: theme.palette.common.black,
    color: theme.palette.common.black,
    backgroundColor: "lavender",
    border: "1px solid white",
  },

  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "0 1rem",
    height: "45px",
    //borderLeft: "1px solid #c4bdbd",
    borderLeft: "1px solid #ceddf2",
    "&:nth-of-type(1)": {
      borderLeft: 0,
    },
  },
}));

export const StyledTableFilterCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    borderLeft: "1px solid #ceddf2",
    padding: ".2rem 1rem",
    "&:nth-of-type(1)": {
      borderLeft: 0,
    },
  },
}));

function TableBodyCell({ children, sx = null }) {
  return (
    <StyledTableCell {...(sx && { sx: sx })}>
      <MDBox
        sx={{
          display: "inline-block",
          minWidth: "100%",
          width: "max-content",
          verticalAlign: "middle",
          fontWeight: 400,
        }}
      >
        {children}
      </MDBox>
    </StyledTableCell>
  );
}

export function TableHeaderCell({ children, width = "150px", onClick, isSorted, canSort = true }) {
  return (
    <StyledTableCell>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        {...(canSort && { minWidth: width, width: "100%" })}
      >
        {children}
        {canSort && (
          <MDBox position="relative" minWidth="1.2rem" ml={1} flexShrink={0}>
            <Tooltip title={`sort asc`} placement="top">
              <Icon
                sx={{
                  position: "absolute",
                  opacity: `${isSorted === "asc" ? 1 : 0.4}`,
                  left: 0,
                  top: -14,
                  fontSize: "1.2rem!important",
                  color: "#0064ff",
                }}
                onClick={onClick}
              >
                keyboard_arrow_up
              </Icon>
            </Tooltip>
            <Tooltip title={`sort desc`} placement="bottom">
              <Icon
                sx={{
                  position: "absolute",
                  opacity: `${isSorted === "desc" ? 1 : 0.4}`,
                  left: 0,
                  top: -4,
                  fontSize: "1.2rem!important",
                  color: "#0064ff",
                }}
                onClick={onClick}
              >
                keyboard_arrow_down
              </Icon>
            </Tooltip>
          </MDBox>
        )}
      </MDBox>
    </StyledTableCell>
  );
}

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor:"white",
  "&:nth-of-type(odd)": {
    //backgroundColor: theme.palette.action.hover,
    backgroundColor: "#e8eff9",
    "&:hover": {
      backgroundColor: "#ceddf2",
    },
  },
  "&:last-child td, &:last-child th": { borderBottom: "1px solid #ceddf2" },
  height: "20px",
}));

export function DebouncedInput({ value: initialValue, onChange, debounce = 500, ...props }) {
  const [value, setValue] = useState(initialValue);
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <TextField
      sx={{
        "& .MuiInputBase-input": {
          padding: ".5rem",
        },
      }}
      style={{ backgroundColor: "white" }}
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      fullWidth
      placeholder="search record"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Search fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
  );
}

export const TableHead_ = ({ table }) => (
  <TableHead sx={{ display: "table-header-group" }}>
    {table.getHeaderGroups().map((headerGroup) => (
      <TableRow key={headerGroup.id}>
        {headerGroup.headers.map((header) => (
          <TableHeaderCell
            key={header.id}
            width={header.column.columnDef.width}
            canSort={header.column.getCanSort()}
            onClick={header.column.getToggleSortingHandler()}
            isSorted={header.column.getIsSorted()}
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
          </TableHeaderCell>
        ))}
      </TableRow>
    ))}
  </TableHead>
);

function FilterInput({ column, table, customFilter }) {
  const columnFilterValue = column.getFilterValue();

  return (
    <DebouncedInput
      type="text"
      name={column.id}
      value={columnFilterValue ?? ""}
      onChange={(v) => {
        if(Boolean(customFilter)){
          customFilter(column.id,v)
        }
        column.setFilterValue(v)
      }}
    />
  );
}

export const TableBody_ = ({ table, customColor = null, customFilter = null }) => (
  <TableBody sx={{backgroundColor:"transparent"}}>
    {table.getHeaderGroups().map((headerGroup) => (
      <TableRow sx={{ backgroundColor: "aliceblue" }} key={headerGroup.id}>
        {headerGroup.headers.map((header, i) => (
          <StyledTableFilterCell key={header.id} sx={{position:"sticky", top:"50px", zIndex:2, backgroundColor:"aliceblue"}}>
            {header.column.getCanFilter() && <FilterInput column={header.column} table={table} customFilter={customFilter} />}
          </StyledTableFilterCell>
        ))}
      </TableRow>
    ))}
    {table.getRowModel().rows.map((row) => (
      <StyledTableRow
        key={row.original?.id ?? row.id}
        {...(Boolean(customColor) && {
          sx: { backgroundColor: `${row.original.bgColor} !important` },
        })}
        {...(row.getIsSelected() && { sx: { backgroundColor: "#e91e6314 !important" } })}
        hover
      >
        {row.getVisibleCells().map((cell) => (
          <TableBodyCell
            key={cell.id}
            {...(Boolean(customColor) && { sx: { border: "1px solid #7c7cee" } })}
          >
            {/* {console.log(row.original?.id ?? row.id)} */}
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableBodyCell>
        ))}
      </StyledTableRow>
    ))}
    {table
      .getFooterGroups()
      .some((footerGroup) =>
        footerGroup.headers.some((column) => column.column.columnDef.footer)
      ) && (
      <TableRow>
        {table.getFooterGroups().map((footerGroup) =>
          footerGroup.headers.map((footer) => (
            <TableBodyCell key={footer.id}>
              <span style={{ fontWeight: "bold" }}>
                {flexRender(footer.column.columnDef.footer, footer.getContext())}
              </span>
            </TableBodyCell>
          ))
        )}
      </TableRow>
    )}
  </TableBody>
);

export const CheckboxHeader = (props) => (
  <Checkbox
    sx={{
      padding: 0,
      "& .MuiSvgIcon-root": {
        borderColor: "#1A73E8",
      },
    }}
    checked={props.table.getIsAllRowsSelected()}
    onChange={props.table.getToggleAllRowsSelectedHandler()}
    indeterminate={props.table.getIsSomeRowsSelected()}
  />
);

export const CheckboxCell = (props) => (
  <Checkbox
    sx={{
      padding: 0,
      "& .MuiSvgIcon-root": {
        borderColor: "#1A73E8",
      },
    }}
    checked={props.row.getIsSelected()}
    onChange={props.row.getToggleSelectedHandler()}
    indeterminate={props.row.getIsSomeSelected()}
  />
);
